import { Box, Center, Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import history from '~/browserHistory';
import { Pressable } from '~/commonComponents';
import { getShouldShowBackgroundCheckBadge } from '~/growthbook';
import { OutlineChevronRightIcon, SolidHeartIcon, SolidShieldCheckIcon, SolidStarIcon } from '@tempmee/react-icons';
import defaultProfile from '~/images/HY_tooth.png';
import { BackgroundCheckStatusEnum, IJob, IUser } from '~/models';
import { CompanyMatchLabel } from '../CandidateOverview/components';

interface IProps {
  hasWorkedWithDSO: boolean;
  hasWorkedWithOffice: boolean;
  jobId: string;
  job: IJob;
  candidate?: IUser;
  showLicense?: boolean;
  backgroundCheckStatus?: BackgroundCheckStatusEnum;
}

export const CandidateProfile = ({
  candidate,
  hasWorkedWithDSO,
  hasWorkedWithOffice,
  job,
  jobId,
  showLicense = false,
  backgroundCheckStatus,
}: IProps) => (
  <Column>
    <Row gap={5}>
      <Center
        direction="column"
        style={{
          position: 'relative',
          width: 94,
          height: 94,
          padding: 2,
          borderRadius: 94,
          border: `3px solid ${candidate?.isFavorite ? colors.favorites[400] : 'transparent'}`,
        }}
      >
        {candidate && candidate.profile_image ? (
          <img
            alt="profile_image"
            src={candidate.profile_image}
            style={{
              width: 94,
              height: 94,
              borderRadius: 94,
              objectFit: 'cover',
            }}
          />
        ) : (
          <Column className="default_circle_container">
            <img src={defaultProfile} alt="profile_image" width="94px" />
          </Column>
        )}
        {candidate?.isFavorite && (
          <Box style={{ position: 'absolute', top: '72%', right: -4 }}>
            <SolidHeartIcon color={colors.favorites[400]} />
          </Box>
        )}
      </Center>
      <Column justify="center">
        <Row>
          <Typography
            as="span"
            bold
            color={colors.neutral[600]}
            size="heading_4"
            numberOfLines={1}
            style={{ margin: '0px 7px 0px 0px', maxWidth: 280 }}
          >
            {`${candidate?.first_name} ${candidate?.last_name}`}
          </Typography>

          <Row align="center" gap={1}>
            <SolidStarIcon size={20} color={colors.success[500]} />
            <Typography as="span" bold size="body_large" color={colors.neutral[600]}>
              {candidate?.average_rating?.toFixed(1) || '5.0'}
            </Typography>
          </Row>

          <Box
            style={{
              alignSelf: 'center',
              height: 16,
              marginInline: 8,
              border: `1px solid ${colors.neutral[600]}`,
            }}
          />

          <Pressable
            onPress={() => {
              history.push(`/dashboard/candidate/${jobId}`, {
                candidateId: candidate?.id,
                state: job?.state,
                profession: job?.profession,
              });
            }}
          >
            <Row align="center" gap={2} style={{ cursor: 'pointer' }}>
              <Typography as="span" bold color={colors.secondary[500]} size="heading_4">
                Profile
              </Typography>
              <OutlineChevronRightIcon size={10} color={colors.secondary[500]} />
            </Row>
          </Pressable>
        </Row>

        {showLicense && (
          <>
            <Typography as="span" color={colors.neutral[600]} size="body_large">
              Drivers License Verified
            </Typography>

            <Row align="center">
              <Typography as="span">
                {candidate?.license?.subProfession ?? candidate?.license?.profession} Credentials
                Verified
              </Typography>
              {backgroundCheckStatus === BackgroundCheckStatusEnum.Clear &&
                getShouldShowBackgroundCheckBadge() && (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        alignSelf: 'center',
                        height: 16,
                        marginLeft: 8,
                        marginRight: 8,
                        border: `1px solid ${colors.neutral[500]}`,
                      }}
                    />
                    <Row align="center" gap={1}>
                      <Typography as="span">Background Checked</Typography>
                      <SolidShieldCheckIcon size={24} color={colors.secondary[500]} />
                    </Row>
                  </>
                )}
            </Row>
          </>
        )}

        <CompanyMatchLabel
          offerOwnerId={job.offer_owner.id}
          professional={candidate}
          hasWorkedWithDSO={hasWorkedWithDSO}
          hasWorkedWithOffice={hasWorkedWithOffice}
        />
      </Column>
    </Row>
  </Column>
);
